// ui/src/helpers/datetime/utc-date.ts
//
// Pure UTC timeline helpers (createUTCDate, formatUTCISO, add/subtract ms, getUTCNow) do not read app state.
// startOfFacilityDayUTC / endOfFacilityDayUTC call getFacilityTimeZone() (Jotai) — use only in app/test contexts
// with a hydrated store; they are unsuitable for standalone scripts or SSR without that wiring.
import { TZDate } from '@date-fns/tz';

import { createTZDate, type DateInput, getFacilityTimeZone } from './core';

/**
 * Creates a TZDate in UTC. If no date is provided, uses the current time.
 */
export function createUTCDate(date?: DateInput): TZDate {
  return createTZDate(date, 'UTC');
}

/**
 * Returns the current instant as a UTC TZDate.
 */
export const getUTCNow = (): TZDate => createUTCDate();

/**
 * Adds milliseconds to a UTC date input (or UTC now when omitted).
 */
export function addUTCMilliseconds(milliseconds: number, date?: DateInput): TZDate {
  const baseMs = (date === undefined ? getUTCNow() : createUTCDate(date)).getTime();
  return createUTCDate(baseMs + milliseconds);
}

/**
 * Subtracts milliseconds from a UTC date input (or UTC now when omitted).
 */
export function subtractUTCMilliseconds(milliseconds: number, date?: DateInput): TZDate {
  return addUTCMilliseconds(-milliseconds, date);
}

/**
 * Formats a date as an ISO 8601 UTC string (e.g. "2024-03-15T12:00:00.000Z").
 * Use when sending dates to the BFF or ECP as string payloads.
 */
export function formatUTCISO(date: DateInput): string {
  // TZDate.toISOString() can emit "+00:00" in some environments (e.g. Vitest); normalize to "Z" for consistent API payloads.
  const iso = createUTCDate(date).toISOString();
  return iso.endsWith('+00:00') ? iso.slice(0, -6) + 'Z' : iso;
}

/**
 * Returns the UTC instant for the start of the facility-local calendar day for `date`.
 * Reads the facility IANA zone via getFacilityTimeZone() (Jotai); requires app store in tests.
 */
export function startOfFacilityDayUTC(date: DateInput): TZDate {
  const facilityDate = createTZDate(date, getFacilityTimeZone());
  facilityDate.setHours(0, 0, 0, 0);
  return createUTCDate(facilityDate.getTime());
}

/**
 * Returns the UTC instant for the end of the facility-local calendar day for `date`.
 * Reads the facility IANA zone via getFacilityTimeZone() (Jotai); requires app store in tests.
 */
export function endOfFacilityDayUTC(date: DateInput): TZDate {
  const facilityDate = createTZDate(date, getFacilityTimeZone());
  facilityDate.setHours(23, 59, 59, 999);
  return createUTCDate(facilityDate.getTime());
}
